import { EventTypes, HISTORY_ACTION_MODE, Indicator, NodeApi } from '@candulabs/craft';
import {
  AlertDangerIcon,
  ArrowFillDownIcon,
  ArrowFillRightIcon,
  css,
  HiddenIcon,
  Inline,
  LightningIcon,
  styled,
  Tooltip,
} from 'canoe';
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { BuiltInComponents, selectionBlacklist } from 'craft/constants';
import { useEditor, useEditorSelector, useNodeDisplayName, useResponsiveCss } from 'craft/hooks';
import { useViewportBreakpoint } from 'craft/state';

import { DropZone } from './DropZone';

import { EditorContext } from '../context/EditorContext';
import { LayerContext } from '../context/LayerContext';

const WrappingChildrenLayer = styled.div`
  ${({ theme }) => css`
    margin-left: ${theme.spaces.xs};
    border-left: 1px solid ${theme.colors.GlitterGrayL10};
    padding-left: 12px;
    transition: ${theme.transitions.default()};

    &.small {
      margin-left: ${theme.spaces.xxs};
      padding-left: 6px;
    }

    &.xsmall {
      margin-left: 2px;
      padding-left: ${theme.spaces.xxs};
    }

    &.isHighlighted {
      border-left: 1px solid ${theme.colors.BrandPurpleL10};
    }

    &.isBeingDragged {
      opacity: 0.3;
    }
  `}
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    margin: ${theme.spaces.xs} 0;

    &.small {
      margin: ${theme.spaces.xxs} 0;
    }

    &.xsmall {
      margin: 2px 0;
    }
  `}
`;

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    position: relative;
    outline: none;
    width: 100%;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    background-color: ${theme.colors.GlitterGrayL10};
    color: ${theme.colors.OnyxGray};
    height: 28px;
    padding: ${theme.spaces.xxs} ${theme.spaces.xs};
    font-size: 11px;
    min-width: max-content;
    border-radius: ${theme.borderRadius.small};
    transition: ${theme.transitions.default('background')};

    margin: 0;

    cursor: pointer;

    .isDraggable {
      &:active,
      &:focus {
        cursor: grabbing;
      }
    }

    &:not(.isDraggable) {
      &:active,
      &:focus {
        cursor: not-allowed;
      }
    }

    &.isErrored {
      border-left: 2px solid ${theme.colors.Danger};
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;

      &:hover {
        outline: none;

        border: 1px solid ${theme.colors.ParadiseRedL30};
        border-left: 2px solid ${theme.colors.Danger};
      }

      &:focus {
        border-radius: ${theme.borderRadius.small};
        border: 2px solid ${theme.colors.Danger};
      }
    }

    &.isSelected {
      border: 2px solid ${theme.colors.BrandPurple};

      &.isErrored {
        border-radius: ${theme.borderRadius.small};
        border: 2px solid ${theme.colors.borderDanger};
      }
    }

    &:not(.isSelected):not(.isErrored) {
      &:hover {
        outline: none;
        border: 1px solid ${theme.colors.BrandPurpleL30};
      }

      &:focus {
        border: 1px solid ${theme.colors.BrandPurple};
      }
    }

    &.isOver {
      background: ${theme.colors.BrandPurpleL10};
    }

    &.isBeingDragged {
      // this class is when the node is being dragged manually via layers
      opacity: 0.3;
      cursor: grabbing;
    }

    &.isNodeBeingDraggedExternally {
      // this class is when the node is being dragged via canvas
      opacity: 1;
      background-color: ${theme.colors.BrandPurple};
      color: ${theme.colors.White};
    }

    &:not(.expandable) {
      & > span:first-of-type {
        opacity: 0.3;
        pointer-events: none;
      }
    }
  `}
`;

export interface NodeLayerProps {
  nodeId: string;
  forceDraggedNode?: boolean;
  isMultistepForm?: boolean;
}

const blackListCanvasNodes = ['Typography', 'ListItem'];

export const NodeLayer: React.FC<NodeLayerProps> = ({
  nodeId,
  forceDraggedNode,
  ...otherProps
}: NodeLayerProps) => {
  const viewportBreakpoint = useViewportBreakpoint();
  const {
    dropZones,
    targetedDropZone,
    draggedNodeIds,
    handleDragStart,
    handleFocus,
    selectedNodeIds,
  } = useContext(LayerContext);

  const { query, actions } = useEditor();
  const { responsiveCss } = useResponsiveCss([nodeId]);
  const { errorNodes, updateExpandedState, scale } = useContext(EditorContext);
  const indicatorPlacement = useEditorSelector((state) => state.indicator?.placement) as
    | Indicator['placement']
    | undefined;

  const node = query.node(nodeId) as NodeApi;
  const nodeGet = node.get();
  const isRootNode = nodeId === 'ROOT';
  const hasNodes = !!nodeGet.data.nodes.length && !blackListCanvasNodes.includes(nodeGet.data.name);
  const [expanded, setExpanded] = useState(isRootNode);
  const timeoutRef = useRef<number>(0);
  const nodeDisplayedName = useNodeDisplayName(nodeId);

  const isCanvasNode = nodeGet.data.isCanvas && !blackListCanvasNodes.includes(nodeGet.data.name);

  const isEmptyCanvas = isCanvasNode && !hasNodes;

  const isDraggable = node.isDraggable();

  const isAfter = nodeGet.data.nodes.some((nId) => targetedDropZone === `${nId}_after`);
  const isDraggingAtParentNodeFirstChild = draggedNodeIds.includes(nodeGet.data.nodes[0]);

  const isDraggingAtSamePosition =
    draggedNodeIds.findIndex((draggedNodeId) => {
      if (isRootNode) {
        return false;
      }
      // the dragged item is over a DZ-after that is just before itself ... we should not render such DZ
      const parentNodeNodes = node.getParent().childNodes();
      return parentNodeNodes[parentNodeNodes.indexOf(draggedNodeId) - 1] === nodeId;
    }) > -1;

  const isOver =
    !isDraggingAtParentNodeFirstChild && isCanvasNode && targetedDropZone === `${nodeId}_inside`;

  const isDroppable =
    expanded || hasNodes || (isOver && isEmptyCanvas && dropZones[nodeId]?.canDropIn);

  const isBeingDragged = draggedNodeIds.includes(nodeId);

  const isSelected = selectedNodeIds.includes(nodeId);

  const isErrored = errorNodes[nodeId];
  const isNodeInMultistepForm =
    nodeGet.data.name === BuiltInComponents.Form && nodeGet.data.nodes.length > 2;

  const isHidden =
    responsiveCss.display === 'none' ||
    (nodeGet.data.custom?.media?.hidden ?? []).indexOf(viewportBreakpoint) > -1;
  const hasActions = (nodeGet.data.custom?.actions ?? []).length > 0;

  const Icon = expanded ? ArrowFillDownIcon : ArrowFillRightIcon;

  useEffect(() => {
    updateExpandedState?.(nodeId, expanded);
  }, [nodeId, expanded]);

  useEffect(() => {
    if (!isOver) {
      window.clearTimeout(timeoutRef.current);
    } else if (!expanded && isOver && isCanvasNode) {
      timeoutRef.current = window.setTimeout(() => {
        setExpanded(true);
        timeoutRef.current = 0;
      }, 800);
    }
  }, [expanded, isOver, isCanvasNode]);

  useEffect(() => {
    if (!expanded && isCanvasNode && selectedNodeIds.length) {
      // run the nodeApi only if the above condition is matched
      if (node.descendants(true).findIndex((nId) => selectedNodeIds.includes(nId)) > -1) {
        setExpanded(true);
      }
    } else if (expanded && !hasNodes) {
      setExpanded(false);
    }
  }, [expanded, selectedNodeIds, isCanvasNode, hasNodes]);

  const isNodeBeingDraggedOverExternally = indicatorPlacement?.parent?.id === nodeId;
  const isNodeBeingDraggedExternally = indicatorPlacement && draggedNodeIds.includes(nodeId);

  const classNames = `
  ${isErrored ? 'isErrored' : ''}
  ${isAfter || isOver || isNodeBeingDraggedOverExternally ? 'isOver' : ''}
  ${isBeingDragged ? 'isBeingDragged' : ''}
  ${isDraggable ? 'isDraggable' : ''}
  ${isSelected ? 'isSelected' : ''}
  ${isNodeBeingDraggedExternally ? 'isNodeBeingDraggedExternally' : ''}
  ${isDroppable ? 'expandable' : ''}`.trim();

  const wrappingChildrenClassNames = `
  ${isBeingDragged ? 'isBeingDragged' : ''}
  ${isAfter ? 'isHighlighted' : ''}
  ${scale}`.trim();

  const toggleExpanded = useCallback(() => setExpanded((oldExpanded) => !oldExpanded), []);

  if (isNodeBeingDraggedExternally && !forceDraggedNode) {
    return null;
  }

  const draggedNodes = isNodeBeingDraggedOverExternally
    ? [...draggedNodeIds].map((draggedNodeId: string) => {
        return <NodeLayer key={draggedNodeId} nodeId={draggedNodeId} forceDraggedNode />;
      })
    : null;

  const isMultistepForm = isNodeInMultistepForm || otherProps.isMultistepForm;
  const children = (
    <>
      {nodeGet.data.nodes?.map((nId, index) => {
        const nodeLayer = <NodeLayer key={nId} nodeId={nId} isMultistepForm={isMultistepForm} />;
        if (!isNodeBeingDraggedOverExternally || index !== indicatorPlacement?.index) {
          return nodeLayer;
        }
        if (indicatorPlacement?.where === 'before') {
          return (
            <>
              {draggedNodes}
              {nodeLayer}
            </>
          );
        }
        return (
          <>
            {nodeLayer}
            {draggedNodes}
          </>
        );
      })}
    </>
  );

  const nodeName = nodeGet.data.name;
  if (!isMultistepForm && selectionBlacklist.indexOf(nodeName) > -1) {
    if (nodeName === BuiltInComponents.FormStep) {
      const { stepId } = nodeGet.data.props ?? {};
      // don't display children of form steps if they're not currently being selected
      const isOnScreen = document.querySelector(`[data-step-id="${stepId}"]`);

      if (!isOnScreen) {
        return null;
      }
    }

    return children;
  }

  return (
    <>
      <Wrapper className={scale} key={nodeId}>
        <Tooltip
          state="danger"
          placement="top"
          disabled={!isErrored}
          content={
            isErrored ? (
              <Inline space="none" align="center">
                <AlertDangerIcon size="s" color="White" mr="xs" />
                {errorNodes[nodeId].message}
              </Inline>
            ) : (
              <Fragment />
            )
          }
        >
          <StyledWrapper
            draggable={isDraggable ? 'true' : 'false'}
            onDragStart={handleDragStart}
            data-cy={`nodelayer-${nodeId}`}
            data-dnd-id={nodeId}
            data-dropzone-id={nodeId}
            data-dropzone-position="inside"
            className={classNames}
            onFocus={handleFocus}
            tabIndex={0}
            onMouseEnter={() =>
              actions.setEventNodes(EventTypes.hovered, [nodeId], HISTORY_ACTION_MODE.IGNORE)
            }
            onMouseLeave={() =>
              actions.setEventNodes(EventTypes.hovered, [], HISTORY_ACTION_MODE.IGNORE)
            }
          >
            {isCanvasNode && (
              <Icon
                onClick={toggleExpanded}
                color="OnyxGrayL20"
                size="s"
                mr="xxs"
                data-cy={`nodelayer-${nodeId}-arrow`}
              />
            )}
            <div css={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <span>{nodeDisplayedName}</span>
              <div css={{ display: 'flex' }}>
                {hasActions && <LightningIcon size="xs" />}
                {isHidden && <HiddenIcon size="xs" />}
              </div>
            </div>
          </StyledWrapper>
        </Tooltip>

        {isDroppable && !isDraggingAtParentNodeFirstChild && (
          <DropZone nodeId={nodeId} position="inside" />
        )}

        {expanded || isNodeBeingDraggedOverExternally ? (
          <WrappingChildrenLayer className={wrappingChildrenClassNames}>
            {isNodeBeingDraggedOverExternally &&
              (nodeGet.data.nodes || []).length === 0 &&
              draggedNodes}

            {children}
          </WrappingChildrenLayer>
        ) : null}

        {!isRootNode && !isBeingDragged && !isDraggingAtSamePosition && (
          <DropZone nodeId={nodeId} position="after" />
        )}
      </Wrapper>
    </>
  );
};
